import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { usePatronSelector } from '../../common/use-patron-selector';
import programSelectors from '../../models/program/selectors';
import {
  useChatbotThreadListQuery,
  useChatbotUpdateThreadMutation,
  useChatbotArchiveThreadMutation,
} from '../../common/queries/use-chatbot-query';
import ChatHistoryItem from '../../components/chatbot/ChatHistoryItem';
import ChatHistorySearch from '../../components/chatbot/ChatHistorySearch';
import {
  trackChatbotThreadArchive,
  trackChatbotThreadRename,
} from '../../models/assistant/analytics/analytics';
import styles from './chat-history-panel.module.scss';

export interface ChatHistoryPanelProps {
  onSelectThread: (
    threadId: string,
    source: 'history' | 'search',
    query?: string
  ) => void;
  initialQuery?: string;
}

export const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  onSelectThread,
  initialQuery,
}) => {
  const { t } = useTranslation();
  const programId = usePatronSelector(programSelectors.getProgramId);
  const programIdStr = String(programId);
  const [searchActive, setSearchActive] = useState(false);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useChatbotThreadListQuery(programIdStr, 20, 'chat', {
      enabled: !!programId,
    });

  const updateMutation = useChatbotUpdateThreadMutation(programIdStr);
  const archiveMutation = useChatbotArchiveThreadMutation(programIdStr);

  const threads = data?.pages?.flatMap((page) => page.data) ?? [];

  // Infinite scroll: use the threadList div as the IntersectionObserver root so
  // the sentinel (inside overflow-y:auto) is correctly watched within its own
  // scroll container rather than the browser viewport.
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = listRef.current;
    const sentinel = sentinelRef.current;
    if (!list || !sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { root: list, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleRename = useCallback(
    (threadId: string, newTitle: string) => {
      updateMutation.mutate(
        { threadId, title: newTitle },
        {
          onSuccess: () => {
            trackChatbotThreadRename({
              thread_id: threadId,
              title_length: newTitle.length,
            });
          },
        }
      );
    },
    [updateMutation]
  );

  const handleArchive = useCallback(
    (threadId: string) => {
      archiveMutation.mutate(
        { threadId },
        {
          onSuccess: () => {
            trackChatbotThreadArchive({ thread_id: threadId });
          },
        }
      );
    },
    [archiveMutation]
  );

  const handleSelectFromHistory = useCallback(
    (threadId: string) => onSelectThread(threadId, 'history'),
    [onSelectThread]
  );

  const handleSelectFromSearch = useCallback(
    (threadId: string, query: string) =>
      onSelectThread(threadId, 'search', query),
    [onSelectThread]
  );

  return (
    <div
      className={styles.chatHistoryPanel}
      data-testid="chat-history-panel"
      role="region"
      aria-label={t('assistant.history', 'History')}
    >
      <ChatHistorySearch
        onSelectThread={handleSelectFromSearch}
        onSearchActiveChange={setSearchActive}
        initialQuery={initialQuery}
      />

      {!searchActive && (
        <div className={styles.threadList} ref={listRef}>
          {isLoading && (
            <div className={styles.loadingState} data-testid="history-loading">
              <span className={styles.loadingText}>
                {t('common.loading', 'Loading...')}
              </span>
            </div>
          )}

          {!isLoading && threads.length === 0 && (
            <div className={styles.emptyState} data-testid="history-empty">
              <div
                className={cx(
                  'text-body-1 text-black-90',
                  styles.emptyStateTitle
                )}
              >
                {t('assistant.noHistory', 'No conversations yet')}
              </div>
              <div className={styles.emptyStateDescription}>
                {t(
                  'assistant.noHistoryDescription',
                  'Your chat conversations will appear here.'
                )}
              </div>
            </div>
          )}

          {!isLoading &&
            threads.map((thread) => (
              <ChatHistoryItem
                key={thread.id}
                thread={thread}
                isSelected={false}
                onSelect={handleSelectFromHistory}
                onRename={handleRename}
                onArchive={handleArchive}
              />
            ))}
          {isFetchingNextPage && (
            <div className={styles.loadingState}>
              <span className={styles.loadingText}>
                {t('common.loading', 'Loading...')}
              </span>
            </div>
          )}
          <div
            ref={sentinelRef}
            className={styles.loadMoreTrigger}
            data-testid="history-load-more-trigger"
          />
        </div>
      )}
    </div>
  );
};

export default ChatHistoryPanel;
